<template>
  <div class="wizard-step-6">
    <div class="section">
      <h4>
        <i class="pi pi-list-check"></i>
        {{ t('Resumo da Franquia') }}
      </h4>

      <!-- Aviso de não edição -->
      <div class="warning-banner">
        <i class="pi pi-exclamation-triangle"></i>
        <div class="warning-content">
          <h5>{{ t('Esta é uma visualização resumida de todas as informações.') }}</h5>
          <p>{{ t('Para editar qualquer campo, volte para a etapa correspondente usando o botão "Voltar".') }}</p>
        </div>
      </div>

      <!-- Cards de Resumo -->
      <div class="summary-grid">
        <!-- Card 1: Informações da Franquia -->
        <div class="summary-card">
          <SummaryCardHeader
            :title="t('Informações da Franquia')"
            icon="pi pi-building"
            variant="secondary"
          />
          <div class="card-body">
            <div class="summary-row">
              <span class="label"><i class="pi pi-building"></i> {{ t('Nome') }}:</span>
              <span class="value">{{ formData.passo1?.nome || '-' }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="pi pi-envelope"></i> {{ t('Email') }}:</span>
              <span class="value">{{ formData.passo1?.email || '-' }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="pi pi-file-text"></i> {{ t('Documento') }}:</span>
              <span class="value">{{ formData.passo1?.cnpj || '-' }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="pi pi-globe"></i> {{ t('País') }}:</span>
              <span class="value">{{ formData.passo1?.pais_nome || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- Card 2: Áreas de Atuação -->
        <div class="summary-card">
          <SummaryCardHeader
            :title="t('Áreas de Atuação')"
            icon="pi pi-globe"
            variant="secondary"
          />
          <div class="card-body">
            <div class="summary-row">
              <span class="label"><i class="pi pi-globe"></i> {{ t('Países') }}:</span>
              <span class="value">{{ areasCount.paises }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="pi pi-map"></i> {{ t('Estados') }}:</span>
              <span class="value">{{ areasCount.estados }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="pi pi-home"></i> {{ t('Cidades') }}:</span>
              <span class="value">{{ areasCount.cidades }}</span>
            </div>
            <div v-if="areasPreview.length > 0" class="areas-preview">
              <span v-for="area in areasPreview" :key="area" class="area-preview-tag">{{ area }}</span>
            </div>
          </div>
        </div>

        <!-- Card 3: Dados Fiscais e Endereço -->
        <div class="summary-card full-width">
          <SummaryCardHeader
            :title="t('Dados Fiscais e Endereço')"
            icon="pi pi-file-text"
            variant="secondary"
          />
          <div class="card-body">
            <div class="summary-row">
              <span class="label"><i class="pi pi-file-text"></i> {{ t('Documento') }}:</span>
              <span class="value">{{ formData.passo3?.cnpj || '-' }}</span>
            </div>
            <div v-if="countryConfig.hasInscricaoEstadual" class="summary-row">
              <span class="label"><i class="pi pi-id-card"></i> {{ t(countryConfig.inscricaoEstadualLabel || '') }}:</span>
              <span class="value">{{ formData.passo3?.inscricao_estadual || '-' }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="pi pi-map-marker"></i> {{ t('Endereço') }}:</span>
              <span class="value">{{ fullAddress }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card de informação -->
    <WizardInfoCard>
      {{ t('Revise todas as informações antes de finalizar. Use o botão "Voltar" para fazer alterações.') }}
    </WizardInfoCard>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SummaryCardHeader from '../../ui/SummaryCardHeader.vue';
import WizardInfoCard from '../../ui/WizardInfoCard.vue';

interface Passo1 {
  nome?: string;
  email?: string;
  cnpj?: string;
  pais_nome?: string;
  [key: string]: unknown;
}

interface AreasAtuacao {
  Countries?: unknown[];
  States?: unknown[];
  Cities?: unknown[];
  [key: string]: unknown;
}

interface Passo2 {
  areas_atuacao?: AreasAtuacao;
  [key: string]: unknown;
}

interface Passo3 {
  cnpj?: string;
  inscricao_estadual?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  [key: string]: unknown;
}

interface FormDataInput {
  passo1?: Passo1;
  passo2?: Passo2;
  passo3?: Passo3;
  [key: string]: unknown;
}

interface CountryConfig {
  hasInscricaoEstadual?: boolean;
  inscricaoEstadualLabel?: string;
  [key: string]: unknown;
}

interface Props {
  formData: FormDataInput;
  countryConfig: CountryConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'validation-change': [isValid: boolean];
}>();

const { t } = useI18n();

// Alias para countryConfig da prop (para manter o template limpo)
const countryConfig = computed<CountryConfig>(() => props.countryConfig);

// Contagem de áreas
const areasCount = computed(() => {
  const areas = props.formData.passo2?.areas_atuacao;
  return {
    paises: areas?.Countries?.length || 0,
    estados: areas?.States?.length || 0,
    cidades: areas?.Cities?.length || 0,
  };
});

// Preview das áreas (primeiras 5)
const areasPreview = computed<string[]>(() => {
  const areas = props.formData.passo2?.areas_atuacao;
  const preview: string[] = [];

  if (areas?.Countries?.length) {
    preview.push(`${areas.Countries.length} ${t('país(es)')}`);
  }
  if (areas?.States?.length) {
    preview.push(`${areas.States.length} ${t('estado(s)')}`);
  }
  if (areas?.Cities?.length) {
    preview.push(`${areas.Cities.length} ${t('cidade(s)')}`);
  }

  return preview.slice(0, 5);
});

// Endereço completo
const fullAddress = computed<string>(() => {
  const p3 = props.formData.passo3;
  if (!p3) return '-';

  const parts = [
    p3.endereco,
    p3.numero,
    p3.complemento,
    p3.bairro,
    p3.cidade,
    p3.estado,
    p3.cep,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : '-';
});

// Formatação de moeda
const formatCurrency = (value: number | string | null | undefined): string => {
  if (!value || value === 0 || value === '0' || value === '') return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numValue);
};
void formatCurrency;

// Sempre válido (read-only)
const isValid = computed<boolean>(() => true);

// Emitir validação
emit('validation-change', isValid.value);
</script>

<style scoped>
.wizard-step-6 {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 20px;
}

.section h4 {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 10px;
}

.section h4 i {
  width: 28px;
  height: 28px;
  background: rgba(var(--app-secondary-color-rgb), 0.12);
  color: var(--tenant-secondary-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

/* Banner de Aviso */
.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fbbf24;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.15);
}

.warning-banner i {
  width: 40px;
  height: 40px;
  background: #f59e0b;
  color: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 0;
}

.warning-content h5 {
  margin: 0 0 6px 0;
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
}

.warning-content p {
  margin: 0;
  font-size: 13px;
  color: #78350f;
  line-height: 1.5;
}

/* Grid de Cards */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.summary-card {
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  border-color: var(--tenant-secondary-color);
}

.summary-card.full-width {
  grid-column: 1 / -1;
}

.card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--surface-border);
}

.summary-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.summary-row .label {
  font-size: 12px;
  color: var(--text-color-secondary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-row .label i {
  color: var(--tenant-secondary-color);
  font-size: 13px;
}

.summary-row .value {
  font-size: 13px;
  color: var(--text-color);
  font-weight: 600;
  text-align: right;
}

.areas-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--surface-border);
}

.area-preview-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(var(--app-secondary-color-rgb), 0.08);
  border: 1px solid rgba(var(--app-secondary-color-rgb), 0.2);
  border-radius: 12px;
  font-size: 12px;
  color: var(--tenant-secondary-color);
  font-weight: 500;
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .summary-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .summary-row .value {
    text-align: left;
  }

  .warning-banner {
    flex-direction: column;
    text-align: center;
  }
}
</style>
